import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Compass, ArrowRight, RefreshCw, ChevronLeft, ChevronRight, Check, Star,
  Feather, Heart, Zap, Wind, Anchor, Map, MessageCircle, AlertCircle, Sunrise, Flame, X,
} from "lucide-react";
import { endpoints as _endpoints } from "@/lib/api";
import { api } from "@/lib/api";
import CompassPatternNudge from "@/components/CompassPatternNudge";
import CompassReadingCard from "@/components/CompassReadingCard";
import N8Emblem from "@/components/N8Emblem";

/**
 * SoulCompass — a 40-question self-discovery reflection that sits at
 * the top of the Soul tab.
 *
 * States:
 *   - `entry` (no result yet)       → Intro card with "Begin My Soul Compass".
 *   - `entry` (result exists)       → Compact snapshot: archetype badge,
 *                                      values, self-connection %, "Open results"
 *                                      / "Retake" actions.
 *   - `assessment`                  → Section header + one question card at a
 *                                      time with a progress ticker.
 *   - `results`                     → Full personalized results panel with
 *                                      the Whole Self radial + Becoming line.
 */

// Small wrapper so we don't have to add three new endpoints entries.
const SoulCompassAPI = {
  questions:  () => api.get("/soul-compass/questions"),
  submit:     (answers) => api.post("/soul-compass/submit", { answers }),
  latest:     () => api.get("/soul-compass/latest"),
  comparison: () => api.get("/soul-compass/comparison"),
};

const WHOLE_ORDER = [
  "Emotional", "Physical", "Mental", "Sleep & Recovery", "Relationships",
  "Purpose", "Creativity", "Self-Connection", "Growth", "Soul / Spirit",
];

export default function SoulCompass() {
  const [stage, setStage] = useState("entry");
  const [sections, setSections] = useState([]);
  const [latest, setLatest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [sIdx, setSIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Whenever the Compass stage changes (entry → assessment → results →
  // becoming), scroll the window to the top so the new experience opens
  // from its natural starting point rather than wherever the previous
  // stage left the scroll position. Skips the first render.
  const isFirst = React.useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [stage]);

  // Load both the question catalog and any saved latest result.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [q, l] = await Promise.all([SoulCompassAPI.questions(), SoulCompassAPI.latest()]);
        if (!alive) return;
        setSections(q.data.sections || []);
        if (l.data && l.data.result) {
          setLatest(l.data);
        }
      } catch (e) { /* silent — soul compass is optional */ }
    })();
    return () => { alive = false; };
  }, []);

  const totalQuestions = useMemo(() => sections.reduce((a, s) => a + s.questions.length, 0), [sections]);
  const answeredCount = useMemo(() => {
    let n = 0;
    for (const s of sections) for (const q of s.questions) {
      const v = answers[q.id];
      if (v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) n += 1;
    }
    return n;
  }, [sections, answers]);

  const beginAssessment = () => {
    setAnswers({});
    setSIdx(0);
    setQIdx(0);
    setStage("assessment");
  };

  const currentSection = sections[sIdx];
  const currentQuestion = currentSection?.questions?.[qIdx];

  const advance = () => {
    if (!currentSection) return;
    if (qIdx + 1 < currentSection.questions.length) {
      setQIdx((v) => v + 1);
    } else if (sIdx + 1 < sections.length) {
      setSIdx((v) => v + 1);
      setQIdx(0);
    } else {
      submit();
    }
  };
  const back = () => {
    if (qIdx > 0) setQIdx((v) => v - 1);
    else if (sIdx > 0) {
      const prev = sections[sIdx - 1];
      setSIdx((v) => v - 1);
      setQIdx(Math.max(0, (prev?.questions?.length || 1) - 1));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await SoulCompassAPI.submit(answers);
      setResult(data.result);
      setLatest({ result: data.result, created_at: data.created_at });
      setStage("results");
      toast.success("Your Soul Compass is ready.");
    } catch (e) {
      toast.error("Could not save your Soul Compass. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openLatestResults = () => {
    if (latest?.result) {
      setResult(latest.result);
      setStage("results");
    }
  };

  return (
    <div className="mb-8" data-testid="soul-compass">
      {stage === "entry" && (
        <>
          <EntryCard
            latest={latest}
            onBegin={beginAssessment}
            onOpen={openLatestResults}
          />
          <div className="mt-6">
            <CompassReadingCard />
          </div>
        </>
      )}
      {stage === "assessment" && currentQuestion && (
        <AssessmentCard
          section={currentSection}
          sectionIndex={sIdx}
          sectionCount={sections.length}
          question={currentQuestion}
          answer={answers[currentQuestion.id]}
          onChange={(v) => setAnswers((cur) => ({ ...cur, [currentQuestion.id]: v }))}
          onNext={advance}
          onBack={back}
          canBack={sIdx > 0 || qIdx > 0}
          isLast={sIdx === sections.length - 1 && qIdx === currentSection.questions.length - 1}
          submitting={submitting}
          answeredCount={answeredCount}
          totalQuestions={totalQuestions}
        />
      )}
      {stage === "results" && result && (
        <ResultsPanel
          result={result}
          takenAt={latest?.created_at || null}
          onRetake={beginAssessment}
          onClose={() => setStage("entry")}
          onOpenBecoming={() => setStage("becoming")}
        />
      )}
      {stage === "becoming" && (
        <BecomingComparison
          onBack={() => setStage("results")}
          onRetake={beginAssessment}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Watermark compass-rose SVG used inside the Soul Compass entry card. */
function CompassRoseArt({ className = "" }) {
  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden>
      <defs>
        <radialGradient id="cr-fill" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E7B694" />
          <stop offset="60%" stopColor="#C56B3F" />
          <stop offset="100%" stopColor="#6E4A38" />
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r="95" fill="none" stroke="#C56B3F" strokeWidth="0.6" opacity="0.55" />
      <circle cx="100" cy="100" r="72" fill="none" stroke="#C56B3F" strokeWidth="0.4" opacity="0.35" />
      <g fill="url(#cr-fill)" opacity="0.9">
        <polygon points="100,15 108,100 100,92 92,100" />
        <polygon points="100,185 108,100 100,108 92,100" />
        <polygon points="15,100 100,92 92,100 100,108" />
        <polygon points="185,100 100,108 108,100 100,92" />
      </g>
      <g stroke="#C56B3F" strokeWidth="0.35" opacity="0.35">
        <line x1="100" y1="5" x2="100" y2="20" />
        <line x1="100" y1="180" x2="100" y2="195" />
        <line x1="5" y1="100" x2="20" y2="100" />
        <line x1="180" y1="100" x2="195" y2="100" />
      </g>
    </svg>
  );
}

function EntryCard({ latest, onBegin, onOpen }) {
  const hasResult = Boolean(latest?.result);
  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-6 md:p-10 text-left"
      style={{
        background:
          "linear-gradient(120deg, #FBF9F5 0%, #F6F1E8 55%, #EFE1D3 100%)",
        borderColor: "#DCD3C7",
      }}
      data-testid="soul-compass-entry"
    >
      {/* Faded compass-rose watermark — sits behind the copy, hinting at
          what the tool is without becoming an illustration lockup. */}
      <div
        aria-hidden
        className="absolute -right-16 -bottom-24 w-[420px] h-[420px] pointer-events-none opacity-[0.32]"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(184,115,85,0.28), rgba(184,115,85,0.05) 55%, transparent 72%)",
        }}
      />
      <CompassRoseArt className="absolute -right-6 -bottom-12 w-[300px] h-[300px] pointer-events-none opacity-[0.22]" />

      <div className="relative flex items-start justify-between gap-8 flex-wrap">
        <div className="flex-1 min-w-[240px] max-w-xl">
          <div
            className="text-[10px] uppercase tracking-[0.28em] inline-flex items-center gap-2"
            style={{ color: "#C56B3F" }}
          >
            <Compass size={12} /> {hasResult ? "Your Soul Compass" : "Self-Discovery"}
          </div>
          <h3
            className="mt-3 font-display text-3xl md:text-4xl tracking-tight leading-tight"
            style={{ color: "#292725" }}
          >
            {hasResult ? "Your Soul Compass, ready when you are." : "Discover What Guides You"}
          </h3>
          <p
            className="mt-4 max-w-xl text-[15px] leading-relaxed"
            style={{ color: "#746C63" }}
          >
            {hasResult ? (
              <>
                Your last reflection is saved. Retake it anytime — the person you&rsquo;re
                becoming keeps unfolding, and your compass moves with you.
              </>
            ) : (
              <>
                Explore your inner experience, uncover your natural strengths and patterns,
                and gain insight into your unique journey as you align with who you are.
                The person you&rsquo;re becoming keeps understanding&hellip; and your compass moves with you.
              </>
            )}
          </p>
          <div
            className="mt-4 text-[11px] uppercase tracking-[0.24em]"
            style={{ color: "#C56B3F", opacity: 0.85 }}
          >
            Estimated time: 5–7 minutes
          </div>
        </div>

        {/* Button + glowing orb, right side. */}
        <div className="relative flex flex-col items-end gap-3 pt-1">
          <div className="relative">
            <button
              type="button"
              onClick={onBegin}
              data-testid="soul-compass-begin"
              className="relative inline-flex items-center gap-2 rounded-full pl-5 pr-14 py-3 text-sm font-medium tab-link"
              style={{
                background: "linear-gradient(120deg, #6E4A38 0%, #C56B3F 100%)",
                color: "#F6F1E8",
                boxShadow: "0 14px 34px -18px rgba(184,115,85,0.7)",
              }}
            >
              <Compass size={14} />
              <span>{hasResult ? "Retake My Soul Compass" : "Begin My Soul Compass"}</span>
            </button>
            {/* Glowing orb — anchored to the right edge of the button. */}
            <span
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 rounded-full pointer-events-none"
              style={{
                right: -12,
                width: 44,
                height: 44,
                background:
                  "radial-gradient(circle at 35% 30%, #FDE0B6 0%, #F0B06A 40%, #C66B37 100%)",
                boxShadow:
                  "0 0 0 3px rgba(255,255,255,0.5), 0 0 28px 6px rgba(240,176,106,0.6), inset 0 -6px 12px rgba(112,66,31,0.38)",
                animation: "soul-orb-pulse 3.6s ease-in-out infinite",
              }}
              data-testid="soul-compass-orb"
            />
          </div>
          {hasResult && (
            <button
              type="button"
              onClick={onOpen}
              data-testid="soul-compass-open"
              className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm border tab-link"
              style={{ borderColor: "#DCD3C7", color: "#292725" }}
            >
              <span>Open my latest results</span>
              <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>

      {hasResult && (
        <div className="relative mt-8 grid gap-3 md:grid-cols-3 text-sm">
          <div className="rounded-md bg-white/60 border border-[hsl(32,30%,72%)]/50 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(30,45%,32%)]">Primary archetype</div>
            <div className="mt-1 font-display text-xl text-[hsl(24,32%,18%)]">
              {latest.result.primary_archetype?.name || "—"}
            </div>
          </div>
          <div className="rounded-md bg-white/60 border border-[hsl(32,30%,72%)]/50 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(30,45%,32%)]">Top values</div>
            <div className="mt-1 text-sm text-[hsl(24,32%,18%)]">
              {(latest.result.top_values || []).join(" · ") || "—"}
            </div>
          </div>
          <div className="rounded-md bg-white/60 border border-[hsl(32,30%,72%)]/50 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(30,45%,32%)]">Self-connection</div>
            <div className="mt-1 font-display text-2xl text-[hsl(30,72%,42%)] tabular-nums">
              {latest.result.self_connection_pct ?? 0}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AssessmentCard({
  section, sectionIndex, sectionCount, question, answer, onChange,
  onNext, onBack, canBack, isLast, submitting, answeredCount, totalQuestions,
}) {
  const canAdvance = validAnswer(question, answer);
  const pct = Math.round((answeredCount / Math.max(1, totalQuestions)) * 100);

  return (
    <div className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-8" data-testid="soul-compass-assessment">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">
          Section {sectionIndex + 1} of {sectionCount} · {section.title}
        </div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] tabular-nums">
          {answeredCount} / {totalQuestions}
        </div>
      </div>
      <div className="mt-2 h-1 rounded-full bg-[hsl(var(--sand))] overflow-hidden">
        <div className="h-full bg-[hsl(var(--terracotta))] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>

      <h4 className="mt-6 font-display text-2xl md:text-3xl leading-tight text-[hsl(24,32%,18%)]">
        {question.prompt}
      </h4>

      <div className="mt-5">
        {question.type === "single" && (
          <SingleChoice question={question} value={answer} onChange={onChange} />
        )}
        {question.type === "multi" && (
          <MultiChoice question={question} value={answer || []} onChange={onChange} />
        )}
        {question.type === "scale5" && (
          <ScaleChoice question={question} value={answer} onChange={onChange} />
        )}
        {question.type === "text" && (
          <textarea
            value={answer || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            placeholder="Write freely…"
            className="w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-[15px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-[hsl(var(--terracotta))]/50 resize-none"
            data-testid="soul-compass-text-input"
          />
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={!canBack}
          className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] disabled:opacity-40 tab-link"
          data-testid="soul-compass-back"
        >
          <ChevronLeft size={14} /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance || submitting}
          className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-medium bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90 disabled:opacity-40 tab-link"
          data-testid="soul-compass-next"
        >
          <span>{isLast ? (submitting ? "Saving…" : "See my Soul Compass") : "Continue"}</span>
          {isLast ? <Check size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>
    </div>
  );
}

function validAnswer(q, v) {
  if (q.type === "single") return typeof v === "string" && v.length > 0;
  if (q.type === "multi")  return Array.isArray(v) && v.length >= (q.min || 1);
  if (q.type === "scale5") return typeof v === "number" && v >= 1 && v <= 5;
  if (q.type === "text")   return true; // text is optional
  return false;
}

function SingleChoice({ question, value, onChange }) {
  return (
    <ul className="grid gap-2" data-testid="soul-compass-single">
      {question.choices.map((c) => {
        const active = value === c.text;
        return (
          <li key={c.text}>
            <button
              type="button"
              onClick={() => onChange(c.text)}
              className={`w-full text-left rounded-xl border px-4 py-3 text-[15px] tab-link ${
                active
                  ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta))]/8 text-[hsl(var(--fg))]"
                  : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/40 text-[hsl(var(--fg))]"
              }`}
              data-testid={`soul-compass-choice-${c.text.slice(0,24).replace(/\W+/g,'-')}`}
            >
              {c.text}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function MultiChoice({ question, value, onChange }) {
  const max = question.max || 3;
  const toggle = (t) => {
    if (value.includes(t)) onChange(value.filter((x) => x !== t));
    else if (value.length < max) onChange([...value, t]);
  };
  return (
    <>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
        Choose {question.min === question.max ? `exactly ${question.min}` : `up to ${max}`}. Selected {value.length}/{max}.
      </div>
      <ul className="flex flex-wrap gap-2" data-testid="soul-compass-multi">
        {question.choices.map((c) => {
          const active = value.includes(c.text);
          return (
            <li key={c.text}>
              <button
                type="button"
                onClick={() => toggle(c.text)}
                className={`rounded-full border px-4 py-1.5 text-sm tab-link ${
                  active
                    ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--fg))]"
                    : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/40"
                }`}
                data-testid={`soul-compass-value-${c.text.toLowerCase()}`}
              >
                {c.text}
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function ScaleChoice({ question, value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="soul-compass-scale">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-12 h-12 rounded-full border font-display text-lg tab-link ${
              active
                ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--fg))]"
                : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
            data-testid={`soul-compass-scale-${n}`}
            aria-label={`${n} of 5`}
          >
            {n}
          </button>
        );
      })}
      <div className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
        1 = Rarely true &nbsp;·&nbsp; 5 = Very true
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

// Format a "Last taken · N days ago" label. Uses relative units up to a
// week, then falls back to a compact absolute date so the header stays
// short at a glance.
function formatTakenLabel(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const diffMs = Date.now() - then.getTime();
  const day = 86_400_000;
  const days = Math.floor(diffMs / day);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "1 week ago";
  if (days < 31) return `${Math.floor(days / 7)} weeks ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function ResultsPanel({ result, takenAt, onRetake, onClose, onOpenBecoming }) {  const navigate = useNavigate();
  const profile = result?.archetype_profile;
  const archetypeName = result?.primary_archetype?.name || "—";
  const secondary = result?.secondary_archetype;
  const secondaryName = secondary?.name;
  const takenLabel = takenAt ? formatTakenLabel(takenAt) : null;

  return (
    <div className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-8" data-testid="soul-compass-results">
      {/* HEADER — Discover Your Archetype */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(30,45%,32%)] inline-flex items-center gap-2">
            <Compass size={12} /> Your Soul Compass
          </div>
          <h3 className="mt-2 font-display text-3xl md:text-4xl tracking-tight text-[hsl(24,32%,18%)]" data-testid="soul-compass-results-title">
            Discover Your Archetype
          </h3>
          <p className="mt-2 text-[15px] text-[hsl(24,22%,32%)]/85 leading-relaxed">
            Understand the unique qualities, patterns, and strengths that shape the way you move through life.
          </p>
          {takenLabel && (
            <p
              className="mt-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]"
              data-testid="soul-compass-last-taken"
            >
              Last taken · {takenLabel}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onOpenBecoming && (
            <button
              type="button"
              onClick={onOpenBecoming}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-[hsl(var(--terracotta))]/40 text-[hsl(24,32%,22%)] hover:bg-[hsl(var(--terracotta))]/8 tab-link"
              data-testid="soul-compass-open-becoming"
            >
              <Sparkles size={13} /> Meet the you you've been becoming
            </button>
          )}
          <button
            type="button"
            onClick={onRetake}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50 tab-link"
            data-testid="soul-compass-retake"
          >
            <RefreshCw size={13} /> Retake
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
          >
            Close
          </button>
        </div>
      </div>

      {/* Pattern nudge — quietly appears when a growth edge shows up in
          recent Journal entries. Placed just under the header so it's the
          first thing members see when the Compass has something to point at. */}
      <div className="mt-5">
        <CompassPatternNudge variant="card" />
      </div>

      {profile ? (
        <ArchetypeExperience
          result={result}
          profile={profile}
          archetypeName={archetypeName}
          secondaryName={secondaryName}
          secondary={secondary}
          onExploreSoulWork={() => navigate("/rituals?soul=work")}
        />
      ) : (
        <div className="mt-8 text-sm text-[hsl(var(--muted-foreground))]">
          Reopen your Compass to see the full archetype experience.
        </div>
      )}

      {/* SUPPORTING PANELS — Whole Self Map, Values, Self-Connection kept
          below the archetype experience as complementary data. */}
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        <section className="rounded-md border hairline bg-white/60 p-5" data-testid="soul-compass-values-panel">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)] inline-flex items-center gap-2">
            <Star size={12} /> Your Chosen Values
          </div>
          <div className="mt-3 font-display text-2xl tracking-tight">
            {(result.top_values || []).join("  ·  ") || "—"}
          </div>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            The values you named as most important. They quietly shape the choices, relationships, and rituals that feel most like you.
          </p>
        </section>

        <section className="rounded-md border hairline bg-white/60 p-5" data-testid="soul-compass-self-conn-panel">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">Self-Connection</div>
          <div className="mt-3 flex items-baseline gap-2">
            <div className="font-display text-4xl text-[hsl(30,72%,42%)] tabular-nums">
              {result.self_connection_pct ?? 0}<span className="text-2xl opacity-70">%</span>
            </div>
          </div>
          <p className="mt-2 text-sm text-[hsl(24,22%,32%)]/90 leading-relaxed">
            {result.self_connection_paragraph}
          </p>
        </section>
      </div>

      <section className="mt-6 rounded-md border hairline bg-white/60 p-5" data-testid="soul-compass-whole-self">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">Your Whole Self Map</div>
        <div className="mt-4 grid gap-6 md:grid-cols-[220px,1fr] items-center">
          <WholeSelfRadial data={result.whole_self} />
          <div className="grid gap-2 sm:grid-cols-2">
            {WHOLE_ORDER.map((label) => {
              const v = result.whole_self?.[label] ?? 0;
              return (
                <div key={label} className="flex items-center gap-3 text-sm">
                  <div className="w-32 text-[hsl(24,32%,22%)]">{label}</div>
                  <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--sand))] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[hsl(var(--terracotta))] transition-[width] duration-500"
                      style={{ width: `${(v / 5) * 100}%` }}
                    />
                  </div>
                  <div className="w-6 text-right tabular-nums text-[hsl(var(--muted-foreground))]">{v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BECOMING STATEMENT */}
      <section className="mt-6 rounded-md border hairline p-6 md:p-8 bg-[hsl(var(--terracotta))]/8" data-testid="soul-compass-becoming-statement">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">Your Becoming Statement</div>
        <p className="mt-3 font-display text-2xl leading-snug italic text-[hsl(24,32%,18%)]">
          {result.becoming_statement}
        </p>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ArchetypeExperience — renders the 15-section personalized profile plus
// summary + Soul Compass line. Kept as a single scrollable panel; each
// section uses a common `SectionCard` component so the visual rhythm stays
// consistent from top to bottom.
// ---------------------------------------------------------------------------
function ArchetypeExperience({ result, profile, archetypeName, secondaryName, secondary, onExploreSoulWork }) {
  const p = profile;
  const personalStrengths = p?.personalized?.strengths_from_life || [];
  return (
    <div className="mt-8 space-y-6" data-testid="archetype-experience">
      {/* 1. YOUR SOUL ARCHETYPE */}
      <SectionCard
        eyebrow="1 · Your Soul Archetype"
        title={archetypeName}
        subtitle={secondaryName ? `with strong ${secondaryName.replace("The ", "")} qualities` : null}
        Icon={Compass}
        testid="section-archetype"
      >
        <p className="text-[15px] leading-relaxed text-[hsl(24,22%,32%)]/90">
          {result?.primary_archetype?.description}
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <MiniList label="Core characteristics" items={p.core_characteristics} testid="archetype-core-chars" />
          <MiniList label="Natural tendencies" items={p.natural_tendencies} testid="archetype-tendencies" />
          <MiniList label="Primary motivations" items={p.primary_motivations} testid="archetype-motivations" />
          <MiniList label="Core values (archetype)" items={p.core_values} testid="archetype-values" />
        </div>
        <p className="mt-4 text-[15px] leading-relaxed text-[hsl(24,22%,32%)]/90 italic">
          {p.approach_to_life}
        </p>
      </SectionCard>

      {/* SECONDARY ARCHETYPE — shown only when tallies were close. Kept
          compact so the primary experience stays the main event. */}
      {secondary && <SecondaryArchetypePanel secondary={secondary} />}

      {/* 2. YOUR CORE ESSENCE */}
      <SectionCard eyebrow="2 · Your Core Essence" title="What is at the heart of who you are?" Icon={Heart} testid="section-essence">
        <p className="text-[15px] leading-relaxed text-[hsl(24,22%,32%)]/90">{p.essence}</p>
      </SectionCard>

      {/* 3. YOUR NATURAL STRENGTHS */}
      <SectionCard eyebrow="3 · Your Natural Strengths" title="Strengths that seem to come naturally to you." Icon={Zap} testid="section-strengths">
        <ul className="grid gap-3 sm:grid-cols-2">
          {(p.strengths || []).map((s) => (
            <li key={s.tag} className="rounded-md border hairline bg-[hsl(var(--paper))] p-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">{s.tag}</div>
              <p className="mt-1 text-sm text-[hsl(24,32%,22%)]">{s.body}</p>
            </li>
          ))}
        </ul>
        {personalStrengths.length > 0 && (
          <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
            <span className="uppercase tracking-[0.2em] text-[hsl(var(--clay))] mr-1">From your life today:</span>
            {personalStrengths.join(" · ")}
          </p>
        )}
      </SectionCard>

      {/* 4. YOUR GIFTS */}
      <SectionCard eyebrow="4 · Your Gifts" title="What you naturally bring into the world." Icon={Sparkles} testid="section-gifts">
        <ul className="grid gap-2">
          {(p.gifts || []).map((g, i) => (
            <li key={i} className="flex items-start gap-2 text-[15px] text-[hsl(24,32%,22%)]">
              <span className="mt-1 w-1 h-1 rounded-full bg-[hsl(var(--clay))] shrink-0" />
              <span>{g}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      {/* 5. HOW YOU'RE WIRED */}
      <SectionCard eyebrow="5 · How You're Wired" title="Your natural way of processing." Icon={Feather} testid="section-wired">
        <dl className="grid gap-3 sm:grid-cols-2">
          {Object.entries({
            experiences: "Experiences",
            information: "Information",
            emotions: "Emotions",
            decisions: "Decisions",
            relationships: "Relationships",
            change: "Change",
            challenges: "Challenges",
          }).map(([key, label]) => (
            p?.wired?.[key] ? (
              <div key={key}>
                <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">{label}</dt>
                <dd className="mt-1 text-sm text-[hsl(24,32%,22%)] leading-relaxed">{p.wired[key]}</dd>
              </div>
            ) : null
          ))}
        </dl>
      </SectionCard>

      {/* 6. YOUR INNER MOTIVATORS */}
      <SectionCard eyebrow="6 · Your Inner Motivators" title="What naturally drives you." Icon={Wind} testid="section-motivators">
        <ul className="grid gap-3 sm:grid-cols-2">
          {(p.inner_motivators || []).map((m) => (
            <li key={m.tag} className="rounded-md border hairline bg-[hsl(var(--paper))] p-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">{m.tag}</div>
              <p className="mt-1 text-sm text-[hsl(24,32%,22%)]">{m.body}</p>
            </li>
          ))}
        </ul>
      </SectionCard>

      {/* 7. WHAT ENERGIZES YOU */}
      <SectionCard eyebrow="7 · What Energizes You" title="Where your natural energy is likely to thrive." Icon={Flame} testid="section-energizes">
        <dl className="grid gap-3 sm:grid-cols-2">
          {Object.entries({
            environments: "Environments",
            activities: "Activities",
            relationships: "Relationships",
            types_of_work: "Types of work",
            experiences: "Experiences",
            expression: "Ways of expressing yourself",
          }).map(([key, label]) => (
            (p?.energizes?.[key]?.length || 0) > 0 ? (
              <div key={key}>
                <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">{label}</dt>
                <dd className="mt-1 text-sm text-[hsl(24,32%,22%)] leading-relaxed">{p.energizes[key].join(" · ")}</dd>
              </div>
            ) : null
          ))}
        </dl>
      </SectionCard>

      {/* 8. WHAT DRAINS YOU */}
      <SectionCard eyebrow="8 · What Drains You" title="Conditions that may be less aligned with your natural wiring." Icon={Wind} testid="section-drains">
        <ul className="grid gap-2 sm:grid-cols-2">
          {(p.drains || []).map((d, i) => (
            <li key={i} className="rounded-md border hairline bg-[hsl(var(--paper))] p-3 text-sm text-[hsl(24,32%,22%)]">
              {d}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] italic">
          This isn't a weakness — it's information about the terrain that suits you.
        </p>
      </SectionCard>

      {/* 9. HOW YOU CONNECT */}
      <SectionCard eyebrow="9 · How You Connect" title="Your natural relational style." Icon={MessageCircle} testid="section-connect">
        <dl className="grid gap-3 sm:grid-cols-2">
          {Object.entries({
            communication: "How you communicate",
            building_connection: "How you build connection",
            expressing_care: "How you express care",
            in_conflict: "How you respond in conflict",
            feeling_understood: "What helps you feel understood",
          }).map(([key, label]) => (
            p?.connect?.[key] ? (
              <div key={key}>
                <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">{label}</dt>
                <dd className="mt-1 text-sm text-[hsl(24,32%,22%)] leading-relaxed">{p.connect[key]}</dd>
              </div>
            ) : null
          ))}
        </dl>
      </SectionCard>

      {/* 10. YOUR PATTERNS */}
      <SectionCard eyebrow="10 · Your Patterns" title="Recurring tendencies — not limitations." Icon={Map} testid="section-patterns">
        <ul className="grid gap-2">
          {(p.patterns || []).map((pat, i) => (
            <li key={i} className="flex items-start gap-2 text-[15px] text-[hsl(24,32%,22%)]">
              <span className="mt-1 w-1 h-1 rounded-full bg-[hsl(var(--clay))] shrink-0" />
              <span>{pat}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      {/* 11. YOUR GROWTH EDGES */}
      <SectionCard eyebrow="11 · Your Growth Edges" title="Where a natural strength may become unbalanced." Icon={Anchor} testid="section-growth-edges">
        <ul className="grid gap-3">
          {(p.growth_edges || []).map((g, i) => (
            <li key={i} className="rounded-md border hairline bg-[hsl(var(--paper))] p-3">
              <div className="flex items-center gap-2 text-[13px] text-[hsl(24,32%,22%)]">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Strength</span>
                <span className="font-medium">{g.strength}</span>
                <ArrowRight size={12} className="text-[hsl(var(--muted-foreground))]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Growth edge</span>
              </div>
              <p className="mt-1 text-sm text-[hsl(24,32%,22%)]">{g.edge}</p>
            </li>
          ))}
        </ul>
      </SectionCard>

      {/* 12. YOUR ARCHETYPE IN BALANCE */}
      <SectionCard eyebrow="12 · Your Archetype in Balance" title="When you're in alignment, you may notice…" Icon={Sunrise} testid="section-in-balance">
        <ul className="grid gap-2">
          {(p.in_balance || []).map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-[15px] text-[hsl(24,32%,22%)]">
              <Check size={13} className="mt-1 text-[hsl(var(--clay))] shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      {/* 13. WHEN YOU'RE OUT OF ALIGNMENT */}
      <SectionCard eyebrow="13 · When You're Out of Alignment" title="Signs your natural tendencies may be leaning too far." Icon={AlertCircle} testid="section-out-of-alignment">
        <ul className="grid gap-2 mb-4">
          {(p.out_of_alignment || []).map((o, i) => (
            <li key={i} className="flex items-start gap-2 text-[15px] text-[hsl(24,32%,22%)]">
              <span className="mt-1 w-1 h-1 rounded-full bg-[hsl(var(--clay))] shrink-0" />
              <span>{o}</span>
            </li>
          ))}
        </ul>
        <div className="mt-2 rounded-md bg-[hsl(var(--sand))]/40 border hairline p-3">
          <p className="text-xs text-[hsl(24,22%,32%)]/90 leading-relaxed">
            If a deeper belief or emotional pattern feels like it's underneath this, Soul Work is where you can explore it — kept intentionally separate from the Compass so each experience does its own work.
          </p>
          <button
            type="button"
            onClick={onExploreSoulWork}
            data-testid="section-out-of-alignment-soul-work"
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--clay))] uppercase tracking-[0.22em] hover:opacity-80"
          >
            <Feather size={11} /> Explore This Through Soul Work
          </button>
        </div>
      </SectionCard>

      {/* 14. YOUR POTENTIAL */}
      <SectionCard eyebrow="14 · Your Potential" title="What your natural qualities can become." Icon={Sparkles} testid="section-potential">
        <p className="text-[13px] text-[hsl(var(--muted-foreground))] mb-2 italic">
          Your archetype suggests qualities you can intentionally cultivate — this is possibility, not prediction.
        </p>
        <ul className="grid gap-2">
          {(p.potential || []).map((v, i) => (
            <li key={i} className="flex items-start gap-2 text-[15px] text-[hsl(24,32%,22%)]">
              <span className="mt-1 w-1 h-1 rounded-full bg-[hsl(var(--clay))] shrink-0" />
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      {/* 15. YOUR NATURAL WAY OF BECOMING */}
      <SectionCard eyebrow="15 · Your Natural Way of Becoming" title="What becoming looks like when you honor who you naturally are." Icon={Compass} testid="section-becoming">
        <p className="font-display text-xl leading-snug italic text-[hsl(24,32%,18%)]">
          {p.becoming}
        </p>
        <p className="mt-4 text-sm text-[hsl(24,22%,32%)]/90 leading-relaxed">
          Becoming isn't about changing who you are. It's about understanding yourself deeply enough to consciously become more of who you are capable of being.
        </p>
      </SectionCard>

      {/* ARCHETYPE SUMMARY */}
      <ArchetypeSummary result={result} profile={p} archetypeName={archetypeName} />

      {/* YOUR SOUL COMPASS — closing line */}
      <section
        className="rounded-md border hairline p-6 md:p-8 bg-[hsl(var(--terracotta))]/8"
        data-testid="section-soul-compass-line"
      >
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(30,45%,32%)] inline-flex items-center gap-2">
          <Compass size={12} /> Your Soul Compass
        </div>
        <p className="mt-3 font-display text-xl md:text-2xl leading-snug italic text-[hsl(24,32%,18%)]">
          {p.soul_compass_line}
        </p>
      </section>
    </div>
  );
}

function SectionCard({ eyebrow, title, subtitle, Icon, children, testid }) {
  return (
    <section
      className="rounded-md border hairline bg-white/60 p-5 md:p-6"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        {Icon && (
          <span className="grid place-items-center w-7 h-7 rounded-full bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]">
            <Icon size={13} />
          </span>
        )}
        <div className="text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--clay))]">{eyebrow}</div>
      </div>
      <h4 className="mt-2 font-display text-xl md:text-2xl tracking-tight text-[hsl(24,32%,18%)]">
        {title}
      </h4>
      {subtitle && (
        <div className="text-sm text-[hsl(var(--muted-foreground))] italic">{subtitle}</div>
      )}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function MiniList({ label, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">{label}</div>
      <div className="mt-1 text-sm text-[hsl(24,32%,22%)]">{(items || []).join(" · ")}</div>
    </div>
  );
}

/**
 * SecondaryArchetypePanel — compact mixed-archetype summary. Shown only
 * when the backend returned a `secondary_archetype`, which happens when
 * the primary tally is within one vote of the runner-up. Intentionally
 * smaller than the primary sections so the primary reading stays central.
 */
function SecondaryArchetypePanel({ secondary }) {
  if (!secondary?.name) return null;
  const label = secondary.name.replace("The ", "");
  return (
    <section
      className="rounded-md border hairline bg-[hsl(var(--sand))]/25 p-5 md:p-6"
      data-testid="archetype-secondary"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-7 h-7 rounded-full bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]">
            <Compass size={13} />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--clay))]">
              A close secondary
            </div>
            <div className="font-display text-lg md:text-xl tracking-tight text-[hsl(24,32%,18%)]">
              With strong <span className="italic">{label}</span> qualities
            </div>
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
          You read as a mix
        </div>
      </div>

      {secondary.description && (
        <p className="mt-3 text-[13px] leading-relaxed text-[hsl(24,22%,32%)]/90">
          {secondary.description}
        </p>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
        {(secondary.top_strengths || []).length > 0 && (
          <div data-testid="archetype-secondary-strengths">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Also strong in</div>
            <div className="mt-1 text-[hsl(24,32%,22%)]">{secondary.top_strengths.join(" · ")}</div>
          </div>
        )}
        {(secondary.top_gifts || []).length > 0 && (
          <div data-testid="archetype-secondary-gifts">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Also brings</div>
            <ul className="mt-1 text-[hsl(24,32%,22%)] space-y-0.5">
              {secondary.top_gifts.map((g, i) => (
                <li key={i} className="italic">{g}</li>
              ))}
            </ul>
          </div>
        )}
        {(secondary.top_motivators || []).length > 0 && (
          <div data-testid="archetype-secondary-motivators">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Also motivated by</div>
            <div className="mt-1 text-[hsl(24,32%,22%)]">{secondary.top_motivators.join(" · ")}</div>
          </div>
        )}
      </div>

      <p className="mt-4 text-xs italic text-[hsl(var(--muted-foreground))]">
        A close secondary means these qualities also show up strongly in you — you don&rsquo;t have to pick one.
      </p>
    </section>
  );
}

function ArchetypeSummary({ result, profile, archetypeName }) {
  const p = profile;
  return (
    <section
      className="rounded-md border hairline bg-[hsl(var(--paper))] p-5 md:p-6"
      data-testid="archetype-summary"
    >
      <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(30,45%,32%)] inline-flex items-center gap-2">
        <Star size={12} /> Archetype Summary
      </div>
      <h4 className="mt-2 font-display text-xl md:text-2xl text-[hsl(24,32%,18%)]">A snapshot to carry with you.</h4>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
        <div>
          <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Your Archetype</dt>
          <dd className="mt-0.5 font-display text-lg text-[hsl(24,32%,18%)]">{archetypeName}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Your Core Essence</dt>
          <dd className="mt-0.5 text-[hsl(24,32%,22%)]">{p.essence}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Your Greatest Strengths</dt>
          <dd className="mt-0.5 text-[hsl(24,32%,22%)]">{(p.strengths || []).map((s) => s.tag).join(" · ")}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Your Natural Gifts</dt>
          <dd className="mt-0.5 text-[hsl(24,32%,22%)]">{(p.gifts || []).join(" · ")}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Your Primary Motivators</dt>
          <dd className="mt-0.5 text-[hsl(24,32%,22%)]">{(p.inner_motivators || []).map((m) => m.tag).join(" · ")}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Your Growth Edges</dt>
          <dd className="mt-0.5 text-[hsl(24,32%,22%)]">{(p.growth_edges || []).map((g) => g.strength).join(" · ")}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Your Alignment</dt>
          <dd className="mt-0.5 text-[hsl(24,32%,22%)]">
            Self-Connection <span className="font-display italic">{result?.self_connection_pct ?? 0}%</span>
            {" · "}
            {(result?.top_values || []).slice(0, 3).join(" · ") || "Values pending"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * A minimal SVG radial chart for the ten Whole-Self areas. Renders as
 * a polygon of 10 vertices — radius scales with the 0-5 answer.
 */
function WholeSelfRadial({ data }) {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 22;
  const n = WHOLE_ORDER.length;
  const points = WHOLE_ORDER.map((label, i) => {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    const v = (data?.[label] || 0) / 5;
    const r = maxR * v;
    return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r, ax: cx + Math.cos(angle) * maxR, ay: cy + Math.sin(angle) * maxR };
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ") + " Z";
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[220px]" aria-hidden>
      {[1, 2, 3, 4, 5].map((r) => (
        <circle key={r} cx={cx} cy={cy} r={(maxR / 5) * r} fill="none" stroke="hsl(32,30%,80%)" strokeOpacity="0.5" />
      ))}
      {points.map((p, i) => (
        <line key={`sp-${i}`} x1={cx} y1={cy} x2={p.ax} y2={p.ay} stroke="hsl(32,30%,80%)" strokeOpacity="0.5" />
      ))}
      <path d={path} fill="hsl(30, 72%, 42%)" fillOpacity="0.18" stroke="hsl(30, 72%, 42%)" strokeWidth="1.5" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={`d-${i}`} cx={p.x} cy={p.y} r="2.5" fill="hsl(30, 72%, 42%)" />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// BecomingComparison — "Meet the You You've Been Becoming"
// Shows a positive-framed diff between the two most recent Compass results.

function BecomingComparison({ onBack, onRetake }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await SoulCompassAPI.comparison();
        if (alive) setData(d);
      } catch { if (alive) setData({ available: false, message: "Could not load your comparison." }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div
      className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-8"
      data-testid="soul-compass-becoming"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(30,45%,32%)] inline-flex items-center gap-2">
            <Sparkles size={12} /> Meet the you you've been becoming
          </div>
          <h3 className="mt-2 font-display text-3xl md:text-4xl tracking-tight text-[hsl(24,32%,18%)]">
            {loading ? "Reading your two most recent Compasses…" : (data?.comparison?.headline || "Your evolution")}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
            data-testid="soul-compass-becoming-back"
          >
            <ChevronLeft size={14} /> Back to results
          </button>
          <button
            type="button"
            onClick={onBack}
            aria-label="Close and return to results"
            data-testid="soul-compass-becoming-close"
            className="p-2 rounded-full border hairline text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--sand))]/40 tab-link"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!loading && data && !data.available && (
        <div className="mt-6 rounded-md border hairline p-6 text-center bg-white/60">
          <p className="text-[15px] text-[hsl(24,22%,32%)]/90 max-w-lg mx-auto">
            {data.message}
          </p>
          <button
            type="button"
            onClick={onRetake}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90 tab-link"
            data-testid="soul-compass-becoming-retake"
          >
            <RefreshCw size={13} /> Retake now
          </button>
        </div>
      )}

      {!loading && data && data.available && (
        <BecomingComparisonBody data={data} />
      )}
    </div>
  );
}

function BecomingComparisonBody({ data }) {
  const c = data.comparison || {};
  const prev = data.previous?.result || {};
  const curr = data.current?.result || {};
  const sc = c.self_connection || {};

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <>
      <div className="mt-4 text-xs text-[hsl(var(--muted-foreground))]">
        Comparing <span className="italic">{fmtDate(data.previous?.created_at)}</span>
        <span className="mx-2 opacity-40">→</span>
        <span className="italic">{fmtDate(data.current?.created_at)}</span>
      </div>

      {/* Self-connection movement */}
      <section className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="rounded-md border hairline bg-white/60 p-5" data-testid="becoming-self-connection">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">Self-Connection</div>
          <div className="mt-3 flex items-baseline gap-3">
            <div className="font-display text-3xl text-[hsl(30,72%,42%)] tabular-nums">
              {sc.current ?? 0}<span className="text-xl opacity-70">%</span>
            </div>
            <div className={`text-sm font-medium ${sc.delta > 0 ? "text-[hsl(140,45%,32%)]" : sc.delta < 0 ? "text-[hsl(24,55%,42%)]" : "text-[hsl(var(--muted-foreground))]"}`}>
              {sc.delta > 0 ? `+${sc.delta}` : sc.delta} vs {sc.previous ?? 0}%
            </div>
          </div>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            {curr.self_connection_paragraph}
          </p>
        </div>

        {/* Archetype shift */}
        <div className="rounded-md border hairline bg-white/60 p-5" data-testid="becoming-archetype">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">Archetype</div>
          {c.archetype_change ? (
            <div className="mt-3 text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">{c.archetype_change.from_name}</span>
              <span className="mx-2 opacity-50">→</span>
              <span className="font-display text-xl italic text-[hsl(24,32%,18%)]">{c.archetype_change.to_name}</span>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                You're moving through a shift in how you naturally move through life.
              </p>
            </div>
          ) : (
            <div className="mt-3">
              <div className="font-display text-xl italic text-[hsl(24,32%,18%)]">
                {curr.primary_archetype?.name || "—"} · steady
              </div>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                Your primary pattern has held. Growth is happening inside it.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Strengthened / Investing / Evolving grid */}
      <section className="mt-6 grid gap-4 md:grid-cols-3" data-testid="becoming-tri-grid">
        <MovementColumn
          title="What's strengthened"
          eyebrow="Growth"
          tone="up"
          items={(c.strengthened || []).map((s) => ({
            label: s.area, delta: `+${s.delta}`, prev: s.previous, curr: s.current,
          }))}
          emptyMsg="No stronger areas yet — that's an honest place to begin."
          testid="becoming-strengthened"
        />
        <MovementColumn
          title="What's investing"
          eyebrow="Ready for care"
          tone="down"
          items={(c.investing || []).map((s) => ({
            label: s.area, delta: `${s.delta}`, prev: s.previous, curr: s.current,
          }))}
          emptyMsg="Nothing is quietly asking for more right now."
          testid="becoming-investing"
        />
        <MovementColumn
          title="What's evolving"
          eyebrow="Held steady"
          tone="flat"
          items={(c.evolving || []).slice(0, 6).map((s) => ({
            label: s.area, delta: "steady", curr: s.value,
          }))}
          emptyMsg="—"
          testid="becoming-evolving"
        />
      </section>

      {/* Values movement */}
      <section className="mt-6 rounded-md border hairline bg-white/60 p-5" data-testid="becoming-values">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">Your Inner Compass</div>
        <div className="mt-3 grid gap-3 md:grid-cols-3 text-sm">
          <ValuesLine label="Kept close" items={c.values?.kept} tone="kept" />
          <ValuesLine label="New this time" items={c.values?.added} tone="added" />
          <ValuesLine label="Released" items={c.values?.released} tone="released" />
        </div>
      </section>

      {/* Becoming statement + closing prompt */}
      <section className="mt-6 rounded-md border hairline p-6 md:p-8 bg-[hsl(var(--terracotta))]/8">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(30,45%,32%)]">Your Becoming Statement · Today</div>
        <p className="mt-3 font-display text-2xl leading-snug italic text-[hsl(24,32%,18%)]">
          {curr.becoming_statement}
        </p>
        {prev.becoming_statement && (
          <details className="mt-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="becoming-previous-statement">
            <summary className="cursor-pointer">See your previous becoming statement</summary>
            <p className="mt-2 italic">{prev.becoming_statement}</p>
          </details>
        )}
      </section>
    </>
  );
}

function MovementColumn({ title, eyebrow, tone, items, emptyMsg, testid }) {
  const toneStyle = tone === "up"
    ? "text-[hsl(140,45%,32%)]"
    : tone === "down"
      ? "text-[hsl(24,55%,42%)]"
      : "text-[hsl(var(--muted-foreground))]";
  return (
    <div className="rounded-md border hairline bg-white/60 p-4" data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.24em] ${toneStyle}`}>{eyebrow}</div>
      <div className="mt-1 font-display text-lg text-[hsl(24,32%,18%)]">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="mt-3 text-sm text-[hsl(var(--muted-foreground))] italic">{emptyMsg}</div>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((it) => (
            <li key={it.label} className="flex items-center justify-between text-sm">
              <span className="text-[hsl(24,32%,20%)]">{it.label}</span>
              <span className={`tabular-nums ${toneStyle}`}>
                {it.prev != null ? (
                  <span className="text-[hsl(var(--muted-foreground))] mr-2 opacity-70">{it.prev} → {it.curr}</span>
                ) : null}
                {typeof it.delta === "string" ? it.delta : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ValuesLine({ label, items, tone }) {
  const dotColor = tone === "added" ? "hsl(140,45%,42%)" : tone === "released" ? "hsl(24,55%,42%)" : "hsl(30,72%,42%)";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] mb-1">{label}</div>
      {(!items || items.length === 0) ? (
        <div className="text-[hsl(var(--muted-foreground))] italic">—</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((v) => (
            <span key={v} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-2.5 py-0.5 text-xs">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />
              {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

